import { z } from 'zod';

/**
 * Schema de validación del formulario de noticias (react-hook-form + zod).
 * Mensajes en español, como el resto de schemas del repo.
 *
 * Las fechas viajan en el formato del input `datetime-local`
 * (`YYYY-MM-DDTHH:mm`, hora local); la conversión a ISO 8601 —lo que exige el
 * back— ocurre solo al enviar, con `localInputToIso`. La imagen no vive aquí:
 * es un archivo en estado aparte validado por el dropzone.
 *
 * `scheduled` es el toggle "Programar publicación": encendido, `publishAt` es
 * obligatorio; apagado, la noticia se publica en el momento del envío y la
 * expiración se compara contra "ahora" (la fecha efectiva de publicación).
 */
export const newsFormSchema = z
  .object({
    title: z
      .string()
      .trim()
      .min(1, 'El título es obligatorio')
      .max(255, 'El título no puede exceder los 255 caracteres'),
    description: z.string(),
    scheduled: z.boolean(),
    publishAt: z.string(),
    pinned: z.boolean(),
    expiresAt: z.string(),
  })
  .superRefine((values, ctx) => {
    if (values.scheduled && !values.publishAt) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'La fecha de publicación es obligatoria',
        path: ['publishAt'],
      });
      return;
    }
    if (!values.expiresAt) return;
    const effectivePublish = values.scheduled ? new Date(values.publishAt) : new Date();
    if (new Date(values.expiresAt) <= effectivePublish) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'La fecha de expiración debe ser posterior a la fecha de publicación',
        path: ['expiresAt'],
      });
    }
  });

export type NewsFormValues = z.infer<typeof newsFormSchema>;

const pad = (value: number) => String(value).padStart(2, '0');

/** ISO 8601 → valor de un input `datetime-local` en hora local (precarga en edición). */
export const isoToLocalInput = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const date = new Date(iso);
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(
    date.getMinutes()
  )}`;
};

/** Valor de un input `datetime-local` (hora local) → ISO 8601, formato del contrato. */
export const localInputToIso = (local: string): string => new Date(local).toISOString();
